import React, { useState } from 'react';
import {
  IconMapPin,
  IconPuzzle,
  IconMoneybag,
  IconCalendar,
  IconInfoCircle,
  IconBuildingCircus,
  IconBrandLinkedin
} from '@tabler/icons-react';

const ProjectOverviewCard = ({ project }) => {
  const projectImage = project?.image || null;

  if (!project) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
        <IconInfoCircle size={32} className="mx-auto mb-3 text-gray-400" />
        <p className="font-medium">No project data available.</p>
      </div>
    );
  }

  return (
    <>
      {/* The main card container */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
        <div className="grid grid-cols-12 gap-5 items-start">
          {/* Left Section: Image and Main Content */}
          <div className="col-span-12 md:col-span-7 flex flex-col sm:flex-row items-start">
            <div className="relative mb-4 sm:mb-0 sm:mr-6 flex-shrink-0">
              <img
                src={
                  projectImage ||
                  "https://via.placeholder.com/110x110?text=Project"
                }
                alt={project.name || "Project"}
                className="w-[110px] h-[110px] rounded-md object-cover border border-gray-200"
              />
            </div>

            <div className="flex flex-col justify-center gap-2 items-start flex-grow">
              <h2 className="text-2xl font-bold text-gray-900 leading-tight">
                {project.name || 'Untitled Project'}
              </h2>
              {project.description && project.description.length > 0 && (
                <div className="text-gray-700 text-sm leading-relaxed">
                  <p className="inline">
                    {project.description}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Section: Key Info */}
          <div className="col-span-12 md:col-span-5 flex flex-col justify-between gap-3 md:pl-4 mt-6 md:mt-0">
            {project.city && project.country && (project.city.trim() !== '' || project.country.trim() !== '') && (
              <div className="flex items-center gap-3 text-gray-700 text-sm">
                <div className="flex items-center gap-2 text-gray-900 font-semibold">
                  <IconMapPin size={18} /> Location:
                </div>
                <span>{`${project.city}, ${project.country}`}</span>
              </div>
            )}
            {typeof project.accepted_applications_count === 'number' && (
              <div className="flex items-center gap-3 text-gray-700 text-sm">
                <div className="flex items-center gap-2 text-gray-900 font-semibold">
                  <IconBuildingCircus size={18} /> Accepted Teams:
                </div>
                <span>{project.accepted_applications_count}</span>
              </div>
            )}
            {project.industry && project.industry.trim() !== '' && (
              <div className="flex items-center gap-3 text-gray-700 text-sm">
                <div className="flex items-center gap-2 text-gray-900 font-semibold">
                  <IconPuzzle size={18} /> Industry:
                </div>
                <span>{project.industry}</span>
              </div>
            )}
            {project.budget !== null && project.budget !== '' && parseFloat(project.budget) > 0 && (
              <div className="flex items-center gap-3 text-gray-700 text-sm">
                <div className="flex items-center gap-2 text-gray-900 font-semibold">
                  <IconMoneybag size={18} /> Budget:
                </div>
                <span>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(project.budget)}</span>
              </div>
            )}
            {project.end_date && project.end_date.trim() !== '' && (
              <div className="flex items-center gap-3 text-gray-700 text-sm">
                <div className="flex items-center gap-2 text-gray-900 font-semibold">
                  <IconCalendar size={18} /> End Date:
                </div>
                <span>{new Date(project.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            )}
            {project.linkedin && project.linkedin.trim() !== '' && (
              <a
                href={project.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-blue-600 text-sm hover:underline"
              >
                <IconBrandLinkedin size={18} /> View LinkedIn
              </a>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectOverviewCard;